import React, { Dispatch, SetStateAction, useState, useContext } from 'react';

export interface IJpmcTransactionInfo {
  JPUid: string;
  ccNumber: string;
  maskedCCNumber: string;
  ccExpiryDate: string;
  cardholderName: string;
  ccType: string;
}

type ContextShape = {
  /** Whether the pop-up modal for updating the credit card info is visible. */
  modalVisible: boolean;
  /** Set the state of `modalVisible` for the JPMC credit card modal. */
  setModalVisible: Dispatch<SetStateAction<boolean>>;
  /** The updated payment information for the JPMC credit card modal. */
  updatedPaymentInfo: IJpmcTransactionInfo | null;
  /** Set the state of `updatedPaymentInfo` for the JPMC credit card modal. */
  setUpdatedPaymentInfo: Dispatch<SetStateAction<IJpmcTransactionInfo | null>>;
  /** Whether we have valid updated payment information from the JPMC credit card modal. */
  hasUpdatedPaymentInfo: boolean;
};

type ContextProps = {
  /** All of the child React elements rendered inside this provider. */
  children?: React.ReactNode;
};

const UpdatePaymentCardModalContext = React.createContext<ContextShape | null>(
  null,
);

/**
 * Safe hook to consume UpdatePaymentCardModalContext.
 * Throws a helpful error if used outside of UpdatePaymentCardModalContextProvider.
 */
export function useUpdatePaymentCardModalContext(): ContextShape {
  const context = useContext(UpdatePaymentCardModalContext);
  if (!context) {
    throw new Error(
      'useUpdatePaymentCardModalContext must be used within a UpdatePaymentCardModalContextProvider. ' +
        'Wrap your component with <UpdatePaymentCardModalContextProvider> or use the <UpdatePaymentCardModal> component.',
    );
  }
  return context;
}

/**
 * The Context Provider for the Update Payment Card Modal.
 * Manages the state for whether the modal is visible, as well as the updated payment information returned from the JPMC iframe.
 * @see UpdatePaymentCardModal for the component that consumes this context and renders the modal itself.
 */
const UpdatePaymentCardModalContextProvider: React.FC<ContextProps> = ({
  children,
}) => {
  const [modalVisible, setModalVisible] = useState(false);
  const [updatedPaymentInfo, setUpdatedPaymentInfo] =
    useState<IJpmcTransactionInfo | null>(null);

  const hasUpdatedPaymentInfo = !!(
    updatedPaymentInfo &&
    updatedPaymentInfo.ccNumber &&
    updatedPaymentInfo.ccExpiryDate &&
    updatedPaymentInfo.cardholderName &&
    updatedPaymentInfo.ccType &&
    updatedPaymentInfo.maskedCCNumber
  );

  return (
    <UpdatePaymentCardModalContext.Provider
      value={{
        modalVisible,
        setModalVisible,
        updatedPaymentInfo,
        setUpdatedPaymentInfo,
        hasUpdatedPaymentInfo,
      }}
    >
      {children}
    </UpdatePaymentCardModalContext.Provider>
  );
};

export default UpdatePaymentCardModalContextProvider;
